import { useMemo } from 'react'

import { PagedList, SearchEmailItemResponse } from 'core'
import EmailStatus from 'core/domain/mailHistory/emailStatus'

import { formatDateOnlyTime, parseZoneFormat } from 'common/dateFormatters'

import { Button, Tag } from '..'
import TableV2 from '../dataDisplay/TableV2'

import './styles.less'

interface EmailsTableProps {
  emails?: PagedList<SearchEmailItemResponse>
  loadingEmails: boolean
  rowKey?: string
  onRow?: any
  onChange?: (filters: { pageNumber: number; pageSize: number; orderColumn: string; orderDescending: boolean }) => any
  onActionClick?: (id: string) => void
  actionIsLoading?: boolean
  isResponsive?: boolean
  showAction?: boolean
  actionLabel?: string
}

const EmailsTable: React.FC<EmailsTableProps> = ({
  emails,
  loadingEmails,
  actionIsLoading,
  rowKey,
  onRow,
  onChange,
  onActionClick,
  isResponsive,
  showAction = true,
  actionLabel = 'Details',
}) => {
  const columns = useMemo(
    () =>
      [].concat(
        isResponsive
          ? [
              {
                title: 'First name',
                isStatic: isResponsive,
                fixed: isResponsive && 'left',
                dataIndex: 'companyUser.firstName',
                key: 'firstName',
                sorter: false,
                render: (_, { companyUser }) => companyUser?.firstName,
              },
              {
                title: 'Last name',
                isStatic: isResponsive,
                fixed: isResponsive && 'left',
                dataIndex: 'companyUser.lastName',
                key: 'lastName',
                sorter: false,
                render: (_, { companyUser }) => companyUser?.lastName,
              },
            ]
          : [],
        [
          {
            title: isResponsive ? 'Work email' : 'Recipient',
            dataIndex: 'receiverEmailAddress',
            key: 'receiverEmailAddress',
            sorter: false,
          },
          {
            title: 'Subject',
            dataIndex: 'subject',
            key: 'subject',
            sorter: false,
          },
          {
            align: 'center',
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            sorter: false,
            render: (status: EmailStatus) => <EmailStatusTag status={status} />,
          },
          {
            title: !isResponsive ? 'InitiatedOn' : 'Date Sent',
            dataIndex: 'initiatedOn',
            key: 'initiatedOn',
            sorter: true,
            defaultSortOrder: 'descend',
            render: (initiatedOn: string) => (
              <span>
                <div>{initiatedOn ? parseZoneFormat(initiatedOn) : 'No Date Sent '} </div>
                <div>{initiatedOn ? formatDateOnlyTime(initiatedOn) : ''}</div>
              </span>
            ),
          },
        ],
        !isResponsive
          ? [
              {
                title: 'DeliveredOn',
                dataIndex: 'sendDate',
                key: 'sendDate',
                sorter: true,
                render: (_: string, record: any) => (
                  <span>
                    <div>{record?.sendDate ? parseZoneFormat(record.sendDate) : 'No Date Sent '} </div>
                    <div>{record?.sendDate ? formatDateOnlyTime(record.sendDate) : ''}</div>
                  </span>
                ),
              },
              {
                title: 'Status Details',
                dataIndex: 'statusDetails',
                key: 'statusDetails',
                sorter: false,
              },
            ]
          : [],
        showAction
          ? [
              {
                align: 'center',
                title: 'Actions',
                isStatic: isResponsive,
                fixed: isResponsive && 'right',
                dataIndex: 'id',
                key: 'id',
                sorter: false,
                render: (id: string, { canResend }: SearchEmailItemResponse) => (
                  <Button
                    className={'w-100'}
                    onClick={() => onActionClick(id)}
                    loading={actionIsLoading}
                    disabled={actionIsLoading || !canResend}
                  >
                    {actionLabel}
                  </Button>
                ),
              },
            ]
          : [],
      ),
    [isResponsive, showAction],
  )

  const onEmailsTableChange = (pagination: any, _: any, sorter: any) => {
    if (onChange) {
      onChange({
        pageNumber: pagination.current,
        pageSize: pagination.pageSize,
        orderColumn: sorter.field || 'sendDate',
        orderDescending: sorter.order !== 'ascend',
      })
    }
  }

  const responsivenessProps = { scroll: { x: 'max-content' } }

  return (
    <TableV2
      className={'emails-table'}
      rowKey={rowKey}
      dataSource={emails?.items}
      onChange={onEmailsTableChange}
      loading={loadingEmails}
      columns={columns}
      onRow={({ id }) => onRow(id)}
      pagination={{
        total: emails?.totalItemCount,
        pageSize: emails?.pageSize,
        current: emails?.pageNumber,
        showSizeChanger: true,
        pageSizeOptions: ['25', '50', '80', '100'],
      }}
      {...(isResponsive && responsivenessProps)}
    />
  )
}

type EmailStatusTagProps = {
  status: EmailStatus
}

const EmailStatusTag = (props: EmailStatusTagProps) => {
  switch (props.status) {
    case EmailStatus.Created:
      return <Tag color={'blue'}>Created</Tag>
    case EmailStatus.Initiated:
      return <Tag color={'cyan'}>Initiated</Tag>
    case EmailStatus.FailedToInitiate:
      return <Tag color={'red'}>Failed Initiated</Tag>
    case EmailStatus.Complaint:
      return <Tag color={'red'}>Complaint</Tag>
    case EmailStatus.Bounced:
      return <Tag color={'red'}>Bounced</Tag>
    case EmailStatus.Delivered:
      return <Tag color={'green'}>Delivered</Tag>
    default:
      return null
  }
}

export default EmailsTable
